/**
 * Types for the rate card creation wizard (frontend-v2 port of the legacy
 * Cloudscape `RateCreateWizard`). The create payload shapes mirror the Rust
 * `CreateRateCardRequest` (`tideway-api/src/dto/rate_card.rs`) and the legacy
 * `RateCardCreatePayload` in `frontend/src/types/index.ts`.
 */
import type {
  ApiSurcharge,
  AllocationRuleCreateData as ApiAllocationRuleCreateData,
  CreateRateLinePayload as ApiCreateRateLinePayload,
  RateCardCreatePayload as ApiRateCardCreatePayload,
  RateCardCreateResponse as ApiRateCardCreateResponse,
} from "@/types/api";

export type CreateRateLinePayload = ApiCreateRateLinePayload;
export type RateCardCreatePayload = ApiRateCardCreatePayload;
export type RateCardCreateResponse = ApiRateCardCreateResponse;
export type AllocationRuleCreateData = ApiAllocationRuleCreateData;

export const EQUIPMENT_SIZES = ["20", "40", "45"] as const;
export const EQUIPMENT_TYPES = ["DV", "HC", "Reefer", "OOG", "DG"] as const;
export const CURRENCIES = ["USD", "EUR", "CNY", "ILS"] as const;

/** A single surcharge row (matches backend `Surcharge`). */
export type Surcharge = ApiSurcharge;

/** Surcharges grouped by equipment size (or the `default` bucket). */
export type SurchargesBySize = Record<string, Surcharge[]>;

/** Per-lane surcharge override keyed by `origin::destination::size`. */
export type SurchargeLineMap = Record<string, Surcharge[]>;

export type WizardMode = "future" | "manual" | "edit" | "spot" | "agent_vessel_spot";

/** Spot sub-type chosen at the start of the spot flow. */
export type SpotType = "booking" | "vessel";

/** Payment provenance for a prepaid-agent vs carrier-direct rate. */
export type SpotPaymentBasis = "prepaid" | "collect";

/** One equipment line (size/type/base FRT) within a trade-lane route pair. */
export interface EquipmentConfig {
  size: string;
  type: string;
  base_rate: number;
}

/** A trade lane: an origin port group → destination port group with equipment. */
export interface RoutePair {
  id: string;
  origin_id: string;
  destination_id: string;
  equipment: EquipmentConfig[];
}

/** A port group — either fetched from the API or created ad-hoc in the wizard. */
export interface PortGroupCreate {
  id: string;
  name: string;
  region: string;
  ports: string[];
  port_names?: Record<string, string>;
}

/** Per-line draft used by the edit / future (source-card) flow. */
export interface WizardLineDraft {
  id: string;
  origin_port: string;
  destination_port: string;
  origin?: string | null;
  destination?: string | null;
  origin_area?: string | null;
  equipment_type?: string | null;
  equipment_size?: string | null;
  is_hazardous?: boolean;
  pre_carriage_pol?: string | null;
  pre_carriage_mot?: string | null;
  currency: string;
  frt: number;
  named_account?: string | null;
  surcharges: Surcharge[];
  max_weight?: number | null;
}

/** A prepaid-agent vessel-spot rate line, pinned to a specific sailing. */
export interface VesselSpotLine {
  key: string;
  vessel: string;
  voyage: string;
  etd: string;
  equipmentSize: string;
  equipmentType: string;
  frt: string;
  currency: string;
  spacesCount: string;
  ddFreeDays: string;
  ddDailyRate: string;
  ddPolicyType: string;
}

export function newVesselLine(): VesselSpotLine {
  return {
    key: `vl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    vessel: "",
    voyage: "",
    etd: "",
    equipmentSize: "40",
    equipmentType: "DV",
    frt: "",
    currency: "USD",
    spacesCount: "",
    ddFreeDays: "",
    ddDailyRate: "",
    ddPolicyType: "destination_demurrage",
  };
}

/** TEU allocation cap scoped to an origin port group. */
export interface TeuLimit {
  port_group_id: string;
  limit: number;
}

/** Restrictions & limits captured on the Restrictions step. */
export interface RateRestrictions {
  min_weight_kg?: number;
  max_weight_kg?: number;
  teu_limits: TeuLimit[];
  commodity_restrictions: string[];
}

/** Container/TEU constraints applied to every booking on the card. */
export interface BookingPoliciesState {
  max_containers?: number;
  min_containers?: number;
  min_teu?: number;
}

/** Card-level D&D defaults captured on the Restrictions step. */
export interface DdDefaultsState {
  free_days?: number;
  daily_rate?: number;
  policy_type?: string;
}

